import React, { useEffect, useState, useCallback, useMemo } from "react";
import { fetchInstrumentos } from "../pre-cadastro-instrumento";
import axios from "axios";
import { Button, Card, Form, Spinner } from "react-bootstrap";
import CreateReavaliacaoModal from "./createReavaliacaoModal";
import ReavaliacaoDetalheModal from "./ReavaliacaoDetalheModal";
import Especialidades from "pages/apps/configuracoes/especialidades";

const CURRENT_YEAR = new Date().getFullYear();

const YEARS = Array.from(
  { length: CURRENT_YEAR - 2020 + 4 },
  (_, i) => 2020 + i,
);

export interface IAvaliacao {
  aplicadores: string[];
  avaliacao_id: number;
  avaliacao_reavaliacao: string;
  coordenador: string;
  data_relatorio: string;
  especialidades: {
    id: number;
    descricao: string;
  }[];
  instrumentos: {
    aplicadores: string[];
    instrumento: string;
    instrumento_id?: number;
  }[];
  paciente: string;
  paciente_id: number;
  status: string;
  supervisor: string;
  relacoes: {
    aplicador: { id: number; apelido: string };
    avaliacao_id: number;
    colaborador_id: number;
    id: number;
    instrumento: { id: number; instrumento: string };
    instrumento_id: number;
  }[];
}

const STATUS_COLOR = {
  planejado: "#49a4e1",
  realizado: "#0aac7e",
  em_andamento: "#3bd6aaeb",
  agendado: "#f0ad4e",
  indeterminado: "#9d9d9d",
} as const;

const STATUS_LABEL = {
  realizado: "Realizada",
  em_andamento: "Em andamento",
  agendado: "Agendada",
  indeterminado: "indeterminado",
  planejado: "Planejada",
} as const;

function formatarData(data: string): string {
  if (!data) {
    return "-";
  }
  const partes = data.split("-");
  return `${partes[2]}/${partes[1]}/${partes[0]}`;
}

export const fetchReavaliacaoView = async (ano: number) => {
  try {
    const res = await axios.get(`/relatorio-parcial/reavaliacao?ano=${ano}`);
    return res.data;
  } catch (err) {
    return null;
  }
};

interface AvaliacaoStatusBadgeProps {
  status:
    | "planejado"
    | "realizado"
    | "agendado"
    | "em_andamento"
    | "indeterminado";
  size?: "small" | "large";
  onClick?: () => void;
}

const AvaliacaoStatusBadge: React.FC<AvaliacaoStatusBadgeProps> = ({
  status,
  size = "large",
  onClick,
}) => {
  const fontSize = size === "small" ? "0.67rem" : "0.8rem";
  const padding = size === "small" ? "3px 5px" : "5px 8px";

  return (
    <span
      onClick={onClick}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: STATUS_COLOR[status],
        color: "#fff",
        padding,
        borderRadius: "4px",
        fontSize,
        fontWeight: 600,
        cursor: onClick ? "pointer" : "default",
        transition: "filter 0.12s",
      }}
      onMouseEnter={(e) => {
        if (onClick)
          (e.currentTarget as HTMLElement).style.filter = "brightness(0.88)";
      }}
      onMouseLeave={(e) => {
        if (onClick) (e.currentTarget as HTMLElement).style.filter = "";
      }}
      title={STATUS_LABEL[status]}
    >
      <i className="mdi mdi-file-document" />
    </span>
  );
};

interface LegendaItemProps {
  label: string;
  status:
    | "planejado"
    | "realizado"
    | "agendado"
    | "indeterminado"
    | "em_andamento";
}

const LegendaItem: React.FC<LegendaItemProps> = ({ label, status }) => (
  <span
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 5,
      fontSize: "0.73rem",
      color: "#555",
    }}
  >
    <AvaliacaoStatusBadge status={status} size="small" />
    {label}
  </span>
);

export default function VisaoAnualGrid() {
  const [instrumentos, setInstrumentos] = useState<any[]>([]);
  const [data, setData] = useState<IAvaliacao[]>([]);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [modalCreate, setModalCreate] = useState(false);
  const [showRelatorioModal, setShowRelatorioModal] = useState(false);
  const [relatorio, setRelatorio] = useState<IAvaliacao | null>(null);
  const [filtroAno, setFiltroAno] = useState<number>(CURRENT_YEAR);
  const [forceUpdate, setForceUpdate] = useState(0);
  const [busca, setBusca] = useState("");
  const [sortDate, setSortDate] = useState(false);
  const [sortName, setSortName] = useState(true);
  const [sorter, setSorter] = useState<"date" | "name">("name");
  const [viewType, setViewType] = useState<"instrumento" | "especialiadade">(
    "instrumento",
  );

  useEffect(() => {
    setData((prev) => {
      return [...prev].sort((a, b) => {
        if (sortDate && sorter === "date") {
          return (
            new Date(b.avaliacao_reavaliacao).getTime() -
            new Date(a.avaliacao_reavaliacao).getTime()
          );
        } else if (!sortDate && sorter === "date") {
          return (
            new Date(a.avaliacao_reavaliacao).getTime() -
            new Date(b.avaliacao_reavaliacao).getTime()
          );
        } else if (sortName && sorter === "name") {
          return b.paciente.localeCompare(a.paciente);
        } else {
          return a.paciente.localeCompare(b.paciente);
        }
      });
    });
  }, [sortDate, sortName, sorter]);

  const normalizeInstrumentos = (
    instrumentos: any,
  ): {
    instrumento: string;
    aplicadores: string[];
    instrumento_id?: number;
  }[] => {
    if (!Array.isArray(instrumentos)) {
      return [];
    }
    return instrumentos.map((instrumento) => {
      if (typeof instrumento === "string") {
        return { instrumento, aplicadores: [], instrumento_id: undefined };
      }
      return {
        instrumento: instrumento.instrumento || String(instrumento),
        aplicadores: Array.isArray(instrumento.aplicadores)
          ? instrumento.aplicadores
          : [],
        instrumento_id: instrumento.instrumento_id,
      };
    });
  };

  const carregarDados = useCallback(async (ano: number) => {
    setLoading(true);
    setErro(null);
    try {
      const [view, inst] = await Promise.all([
        fetchReavaliacaoView(ano),
        fetchInstrumentos(),
      ]);
      setData(
        (view || []).map((item: any) => ({
          ...item,
          instrumentos: normalizeInstrumentos(item.instrumentos),
        })),
      );
      setInstrumentos(inst?.data || []);
    } catch (err) {
      setErro("Erro ao carregar as avaliações e reavaliações");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    carregarDados(filtroAno);
  }, [filtroAno, carregarDados, forceUpdate]);

  const handleAbrirRelatorio = (relatorioData: any) => {
    const InstrumentoAplicador = new Map<string, string[]>();
    relatorioData.relacoes.forEach((e) => {
      const instrumento = e.instrumento.instrumento;
      const aplicador = e.aplicador?.apelido;
      const aplicadores = InstrumentoAplicador.get(instrumento);
      InstrumentoAplicador.set(
        instrumento,
        aplicadores ? aplicadores.concat(aplicador) : [aplicador],
      );
    });
    relatorioData.instrumentos = [];
    InstrumentoAplicador.forEach((v, k, m) => {
      relatorioData.instrumentos.push({
        instrumento: k,
        aplicadores: v,
      });
    });
    setRelatorio(relatorioData);
    setShowRelatorioModal(true);
  };

  const handleFechartModal = () => {
    setShowRelatorioModal(false);
  };

  const handleCriarReavaliacao = () => {
    setModalCreate(true);
  };

  const handleFechaModalCreate = () => {
    setModalCreate(false);
    setForceUpdate(forceUpdate + 1);
  };

  const handleReRender = () => {
    setForceUpdate(forceUpdate + 1);
  };

  const dadosFiltrados = useMemo(() => {
    return busca.trim()
      ? data.filter((d) =>
          (d.paciente ?? "").toLowerCase().includes(busca.trim().toLowerCase()),
        )
      : data;
  }, [data, busca]);
  const especialidadesUnicas = useMemo(() => {
    return [
      ...new Map(
        data
          .flatMap((d) => d.especialidades ?? [])
          .filter((e) => e && e.id != null)
          .map((e) => [e.id, e]),
      ).values(),
    ].toReversed()

  }, [data]);

  return (
    <Card
      className="shadow-sm"
      style={{ borderRadius: 10, border: "1px solid #eee" }}
    >
      <div
        style={{
          padding: "14px 20px",
          borderBottom: "1px solid #f0f0f0",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 10,
        }}
      >
        <Form.Control
          type="text"
          placeholder="Buscar paciente..."
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          style={{
            maxWidth: 240,
            height: 34,
            fontSize: "0.82rem",
            borderRadius: 6,
          }}
        />

        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Form.Select
            style={{
              height: 34,
              fontSize: "0.82rem",
              borderRadius: 6,
              minWidth: 100,
            }}
            value={filtroAno}
            onChange={(e) =>
              setFiltroAno(parseInt((e.target as HTMLSelectElement).value))
            }
          >
            {YEARS.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </Form.Select>

          <Button
            variant="primary"
            size="sm"
            onClick={handleCriarReavaliacao}
            style={{
              borderRadius: 6,
              padding: "6px 12px",
              fontSize: "0.82rem",
            }}
          >
            <span
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                flexDirection: "row",
              }}
            >
              <i className="mdi mdi-plus" />
              Nova
            </span>
          </Button>
        </div>
      </div>

      <Card.Body style={{ padding: "12px 20px 18px" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "6px 16px",
              marginBottom: 14,
            }}
          >
            <LegendaItem status="indeterminado" label="Não agendada" />
            <LegendaItem status="planejado" label="Planejada" />
            <LegendaItem status="agendado" label="Agendada" />
            <LegendaItem status="em_andamento" label="Em andamento" />
            <LegendaItem status="realizado" label="Realizada" />
          </div>

          <Button
            variant={
              viewType === "instrumento"
                ? "outline-primary"
                : "outline-secondary"
            }
            onClick={() =>
              setViewType((prev) =>
                prev === "instrumento" ? "especialidade" : "instrumento",
              )
            }
          >
            Visualização: <b>{viewType}</b>
          </Button>
        </div>

        {loading ? (
          <div
            className="text-center py-5"
            style={{ color: "#999", fontSize: "0.85rem" }}
          >
            <Spinner animation="border" size="sm" className="me-2" />
            Carregando...
          </div>
        ) : erro ? (
          <div
            style={{
              textAlign: "center",
              padding: "20px",
              color: "#dc3545",
              backgroundColor: "#fff5f5",
              borderRadius: 8,
              fontSize: "0.85rem",
            }}
          >
            {erro}
          </div>
        ) : (
          <>
            {!loading && (
              <div
                style={{
                  fontSize: "0.72rem",
                  color: "#bbb",
                  marginBottom: 6,
                }}
              >
                {dadosFiltrados.length} paciente
                {dadosFiltrados.length !== 1 ? "s" : ""}
                {busca
                  ? ` encontrado${dadosFiltrados.length !== 1 ? "s" : ""}`
                  : ""}
              </div>
            )}

            <div
              style={{
                overflowX: "auto",
                overflowY: "visible",
              }}
            >
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: "0.8rem",
                  tableLayout: "auto",
                  minWidth: 900,
                }}
              >
                <thead>
                  <tr>
                    <th style={thStyle({ minWidth: 90 })}>Status</th>

                    <th
                      onClick={() => {
                        if (sorter === "name") {
                          setSortName((prev) => !prev);
                        } else {
                          setSorter("name");
                        }
                      }}
                      style={thStyle({
                        minWidth: 160,
                        position: "sticky",
                        left: 0,
                        zIndex: 2,
                        boxShadow: "2px 0 5px rgba(0,0,0,0.06)",
                        cursor: "pointer",
                      })}
                    >
                      Paciente
                      <i
                        className={`mdi mdi-chevron-${
                          sortName ? "up" : "down"
                        }`}
                      />
                    </th>

                    <th style={thStyle({ minWidth: 110 })}>Coordenador</th>
                    <th style={thStyle({ minWidth: 110 })}>Supervisor</th>

                    <th
                      onClick={() => {
                        if (sorter === "date") {
                          setSortDate((prev) => !prev);
                        } else {
                          setSorter("date");
                        }
                      }}
                      style={thStyle({
                        minWidth: 100,
                        cursor: "pointer",
                        backgroundColor:
                          sorter === "date" ? "#bbbbbb" : "#f8f9fa",
                      })}
                    >
                      Data
                      <i
                        className={`mdi mdi-chevron-${
                          sortDate ? "up" : "down"
                        }`}
                      />
                    </th>

                    {viewType === "instrumento"
                      ? instrumentos.map((inst) => (
                          <th
                            key={inst.id}
                            style={thStyle({
                              minWidth: 80,
                              textAlign: "center",
                            })}
                          >
                            {inst.instrumento}
                          </th>
                        ))
                      : especialidadesUnicas.map((esp) => (
                          <th
                            key={esp.id}
                            style={thStyle({
                              minWidth: 80,
                              textAlign: "center",
                            })}
                          >
                            {esp.descricao}
                          </th>
                        ))}
                  </tr>
                </thead>

                <tbody>
                  {dadosFiltrados.length === 0 ? (
                    <tr>
                      <td
                        colSpan={
                          5 +
                          (viewType === "instrumento"
                            ? instrumentos.length
                            : especialidadesUnicas.length)
                        }
                        style={{
                          textAlign: "center",
                          padding: "40px 20px",
                          color: "#bbb",
                          fontSize: "0.85rem",
                        }}
                      >
                        {busca
                          ? `Nenhum paciente encontrado para "${busca}".`
                          : "Nenhuma avaliação ou reavaliação encontrada."}
                      </td>
                    </tr>
                  ) : (
                    dadosFiltrados.map((item, rowIdx) => (
                      <GridRow
                        key={`${item.paciente_id}-${item.avaliacao_id}`}
                        setData={setData}
                        item={item}
                        rowIdx={rowIdx}
                        instrumentos={
                          viewType === "instrumento"
                            ? instrumentos
                            : especialidadesUnicas
                        }
                        onAbrirRelatorio={handleAbrirRelatorio}
                        viewType={viewType}
                      />
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Card.Body>

      <CreateReavaliacaoModal
        instrumentos={instrumentos}
        view={data}
        show={modalCreate}
        onHide={handleFechaModalCreate}
      />

      <ReavaliacaoDetalheModal
        status={relatorio?.status!}
        id={relatorio?.avaliacao_id!}
        especialidades={relatorio?.especialidades}
        instrumentos={relatorio?.instrumentos!}
        paciente_id={relatorio?.paciente_id!}
        paciente={relatorio?.paciente!}
        show={showRelatorioModal}
        reRender={handleReRender}
        onHide={handleFechartModal}
        data={relatorio}
      />
    </Card>
  );
}

interface GridRowProps<T> {
  item: IAvaliacao;
  rowIdx: number;
  instrumentos: any[];
  viewType: "instrumento" | "especialidade";
  setData: (value: T | ((prev: T) => T)) => void;
  onAbrirRelatorio: (relatorio: any) => void;
}

const GridRow: React.FC<GridRowProps<IAvaliacao[]>> = ({
  item,
  rowIdx,
  instrumentos,
  setData,
  viewType,
  onAbrirRelatorio,
}) => {
  const [hovered, setHovered] = useState(false);
  const [hoveredInst, setHoveredInst] = useState<string | null>(null);

  const bg = hovered ? "#f0faf7" : rowIdx % 2 === 0 ? "#fff" : "#fafafa";

  const handleAssociarDissociar = async (
    associar: boolean,
    avaliacaoId: number,
    instrumentoId: number,
    instrumento: string,
  ) => {
    try {
      if (associar) {
        try {
          await axios.post("/relatorio-parcial/associar", [
            {
              avaliacao_id: avaliacaoId,
              instrumento_id: instrumentoId,
            },
          ]);

          setData((prev) =>
            prev.map((p) =>
              p.avaliacao_id === item.avaliacao_id
                ? {
                    ...p,
                    instrumentos: [
                      ...p.instrumentos,
                      {
                        instrumento,
                        aplicadores: [],
                      },
                    ],
                  }
                : p,
            ),
          );
        } catch {}
      } else {
        await axios.delete("/relatorio-parcial/dissociar", {
          data: [
            {
              avaliacao_id: avaliacaoId,
              instrumento_id: instrumentoId,
            },
          ],
        });
        setData((prev) =>
          prev.map((p) =>
            p.avaliacao_id === item.avaliacao_id
              ? {
                  ...p,
                  instrumentos: p.instrumentos.filter(
                    (i) => i.instrumento !== instrumento,
                  ),
                }
              : p,
          ),
        );
      }
    } catch {
      // TODO toast de erro
    }
  };

  const handlePlanejar = async (pacienteId: number, paciente: string) => {
    const novaReavaliacao = (
      await axios.post(`/relatorio-parcial/paciente/${pacienteId}`, {
        referente_ao_periodo: "um ano retroativo",
        avaliacao_reavaliacao: `${new Date(0).toISOString()}`,
      })
    ).data;
    setData((prev) =>
      prev.map((av) =>
        av.paciente_id === pacienteId
          ? {
              ...av,
              avaliacao_reavaliacao: `${new Date(0).toISOString()}`,
              avaliacao_id: novaReavaliacao.id,
              status: "planejado",
              instrumentos: [],
            }
          : av,
      ),
    );
  };

  return (
    <tr
      style={{
        borderBottom: "1px solid #f0f0f0",
        backgroundColor: bg,
        transition: "background-color 0.1s",
        cursor: "pointer",
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <td style={tdStyle()}>
        <AvaliacaoStatusBadge
          status={item.status}
          onClick={() => onAbrirRelatorio(item)}
        />
      </td>
      <td
        style={{
          ...tdStyle({ fontWeight: 600, color: "#2c3e35" }),
          position: "sticky",
          left: 0,
          backgroundColor: bg,
          zIndex: 1,
          boxShadow: "2px 0 5px rgba(0,0,0,0.05)",
          transition: "background-color 0.1s",
        }}
      >
        {item.paciente ?? "—"}
      </td>
      <td style={tdStyle({ color: "#666" })}>{item.coordenador ?? "—"}</td>
      <td style={tdStyle({ color: "#666" })}>{item.supervisor ?? "—"}</td>
      <td style={tdStyle({ color: "#666" })}>
        {item.avaliacao_reavaliacao
          ? new Date(item.avaliacao_reavaliacao).getTime() ===
            new Date(0).getTime()
            ? "—"
            : formatarData(item.avaliacao_reavaliacao.slice(0, 10))
          : "—"}
      </td>
      {(instrumentos || []).map((inst: any) => {
        const possui =
          viewType === "instrumento"
            ? item.instrumentos.some((i) => i.instrumento === inst.instrumento)
            : item.especialidades.some((e) => e.id === inst.id);

        return (
          <td
            key={inst.id}
            onMouseEnter={() => {
              if (viewType === "instrumento") {
                setHoveredInst(inst.instrumento);
              }
            }}
            onMouseLeave={() => {
              if (viewType === "instrumento") {
                setHoveredInst(null);
              }
            }}
            onClick={async () => {
              if (viewType !== "instrumento") return;

              const possuiInstrumento = item.instrumentos.some(
                (i) => i.instrumento === inst.instrumento,
              );

              if (item.avaliacao_reavaliacao && item.status !== "realizado") {
                await handleAssociarDissociar(
                  !possuiInstrumento,
                  item.avaliacao_id,
                  inst.id,
                  inst.instrumento,
                );
              } else if (item.status !== "realizado") {
                await handlePlanejar(item.paciente_id, item.paciente);
              }
            }}
            style={tdStyle({
              textAlign: "center",
              backgroundColor:
                viewType === "instrumento" && hoveredInst === inst.instrumento
                  ? "#c2f8e8"
                  : "",
            })}
          >
            {possui ? (
              <i
                className="mdi mdi-check-circle"
                style={{ color: "#0aac7e", fontSize: "1rem" }}
              />
            ) : (
              "—"
            )}
          </td>
        );
      })}
    </tr>
  );
};

function thStyle(extra?: React.CSSProperties): React.CSSProperties {
  return {
    position: "sticky",
    top: 0,
    zIndex: 10,
    padding: "9px 10px",
    fontWeight: 600,
    borderBottom: "2px solid #ebebeb",
    whiteSpace: "nowrap",
    fontSize: "0.72rem",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    color: "#888",
    backgroundColor: "#f8f9fa",
    ...extra,
  };
}

function tdStyle(extra?: React.CSSProperties): React.CSSProperties {
  return {
    padding: "7px 10px",
    whiteSpace: "nowrap",
    verticalAlign: "middle",
    ...extra,
  };
}
